import React, { useMemo, useRef, useState } from "react";
import moment from "moment";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ColGroupDef, GridApi } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import ReportPageShell from "../components/ReportPageShell";
import MethodologyPanel from "../components/MethodologyPanel";
import StatCard from "../components/StatCard";
import { SecondaryButton, ToggleButton } from "../../../components/Button";
import { defaultFilters, toQueryParams } from "../shared/filters";
import { useDetailQuery, DetailResponse } from "../shared/useDetailQuery";
import { centsToUsd, formatCount, formatMetric, METRIC_LABELS } from "../shared/format";
import { buildMethodologyCsvRows } from "../shared/metricDefinitions";
import { MetricKey } from "../api/types";

type Mode = "transactions" | "items";

const TOOLTIP =
  "THE reconciliation tool. One row per transaction (or order item), each carrying its day assignment, " +
  "tender, inclusion reason, full money ledger, and its CONTRIBUTION to every metric. The contribution " +
  "columns sum to the Totals, which reconcile exactly to the summary pages for the same filter. Sort/filter " +
  "straight to the rows behind any mismatch. Excluded rows are shaded — the Inclusion Reason says why.";

// Stat-card order per mode (filtered to whatever the engine returns in totals).
const TOTAL_METRICS: Record<Mode, MetricKey[]> = {
  transactions: ["Gross", "NetSales", "Refunds", "TransactionCount", "TransactionAmount"],
  items: ["Units", "ItemRevenue", "ItemTax", "ItemDiscount"],
};

const LIMIT_OPTIONS = [500, 2000, 10000, 50000];
const DEFAULT_LIMIT = 2000;

// ---- shared column factories ----
const money = (field: string, headerName: string, extra: ColDef = {}): ColDef => ({
  field,
  headerName,
  type: "rightAligned",
  width: 130,
  valueFormatter: (p) => centsToUsd(p.value as number),
  ...extra,
});
const int = (field: string, headerName: string, extra: ColDef = {}): ColDef => ({
  field,
  headerName,
  type: "rightAligned",
  width: 110,
  valueFormatter: (p) => formatCount(p.value as number),
  ...extra,
});
const text = (field: string, headerName: string, extra: ColDef = {}): ColDef => ({
  field,
  headerName,
  width: 130,
  ...extra,
});

// Green "IN" / red "OUT" cell for the Included flag.
const inclusionCol = (): ColDef => ({
  headerName: "In/Out",
  field: "included",
  pinned: "left",
  width: 90,
  valueGetter: (p) => (p.data?.included ? "IN" : "OUT"),
  cellStyle: (p) => ({
    fontWeight: 700,
    color: p.value === "IN" ? "#15803d" : "#b91c1c",
  }),
});

function transactionColumns(): (ColDef | ColGroupDef)[] {
  return [
    {
      headerName: "Inclusion",
      children: [inclusionCol(), text("businessDay", "Business Day", { pinned: "left", width: 120 })],
    },
    {
      headerName: "Order / Transaction",
      children: [
        int("orderLegacyId", "Legacy Order #"),
        text("orderId", "Order Id", { width: 150 }),
        text("transactionId", "Txn Id", {
          width: 150,
          valueFormatter: (p) => (p.value as string) ?? "(synthetic)",
        }),
        text("eventName", "Event", { width: 160 }),
        text("vendorId", "Vendor Id", { width: 150 }),
        text("locationId", "Location Id", { width: 150 }),
      ],
    },
    {
      headerName: "Day assignment",
      children: [
        text("timeZone", "Time Zone", { width: 130 }),
        text("dayBoundary", "Day Boundary", { width: 230 }),
        text("transactionAtUtc", "Txn At (UTC)", {
          width: 170,
          valueFormatter: (p) =>
            p.value ? moment.utc(p.value as string).format("YYYY-MM-DD HH:mm") : "",
        }),
      ],
    },
    {
      headerName: "Status / tender",
      children: [
        text("orderStatus", "Order Status", { width: 120 }),
        text("refundKind", "Refund Kind", { width: 110, valueFormatter: (p) => (p.value as string) ?? "" }),
        text("transactionStatus", "Txn Status", { width: 120 }),
        text("tender", "Tender", { width: 130, valueFormatter: (p) => (p.value as string) ?? "" }),
        {
          headerName: "Real $?",
          field: "isRealMoney",
          width: 90,
          valueGetter: (p) => (p.data?.isRealMoney ? "Real" : "Fake"),
          cellStyle: (p) => ({ color: p.value === "Real" ? "#15803d" : "#b45309" }),
        },
      ],
    },
    {
      headerName: "Order money ledger",
      children: [
        money("subtotal", "Subtotal"),
        money("tax", "Tax"),
        money("tip", "Tip"),
        money("surcharge", "Service Fee"),
        money("lineSubtotal", "Line Subtotal"),
        money("lineDiscount", "Line Discount"),
        money("orderDiscount", "Order Discount"),
        money("refundGross", "Refund Gross"),
        money("realMoneyCollected", "Real $ Collected"),
        money("transactionAmount", "Txn Amount"),
      ],
    },
    {
      headerName: "Contribution to totals  (Σ = Totals)",
      children: [
        money("grossContribution", "Gross"),
        money("netContribution", "Net"),
        int("transactionCountContribution", "Txn Count"),
        money("transactionAmountContribution", "Txn Amount"),
        money("refundsContribution", "Refunds"),
        {
          headerName: "Anchor?",
          field: "isOrderAnchor",
          width: 90,
          valueGetter: (p) => (p.data?.isOrderAnchor ? "Yes" : ""),
        },
      ],
    },
    text("inclusionReason", "Inclusion Reason", { width: 300 }),
  ];
}

function orderItemColumns(): (ColDef | ColGroupDef)[] {
  return [
    {
      headerName: "Inclusion",
      children: [inclusionCol(), text("businessDay", "Business Day", { pinned: "left", width: 120 })],
    },
    {
      headerName: "Order / Item",
      children: [
        text("orderId", "Order Id", { width: 150 }),
        text("orderItemId", "Order Item Id", { width: 150 }),
        text("itemId", "Item Id", { width: 150, valueFormatter: (p) => (p.value as string) ?? "" }),
        text("name", "Item", { width: 200 }),
        text("eventId", "Event Id", { width: 150 }),
        text("vendorId", "Vendor Id", { width: 150 }),
      ],
    },
    {
      headerName: "Day assignment",
      children: [
        text("dayBoundary", "Day Boundary", { width: 230 }),
        text("transactionAtUtc", "Txn At (UTC)", {
          width: 170,
          valueFormatter: (p) =>
            p.value ? moment.utc(p.value as string).format("YYYY-MM-DD HH:mm") : "",
        }),
        text("orderStatus", "Order Status", { width: 120 }),
      ],
    },
    {
      headerName: "Line money ledger",
      children: [
        int("quantity", "Qty"),
        money("unitPrice", "Unit Price"),
        money("lineSubtotal", "Line Subtotal"),
        money("lineTax", "Line Tax"),
        money("lineDiscount", "Line Discount"),
      ],
    },
    {
      headerName: "Contribution to totals  (Σ = Totals)",
      children: [
        int("unitsContribution", "Units"),
        money("itemRevenueContribution", "Item Revenue"),
        money("itemTaxContribution", "Item Tax"),
        money("itemDiscountContribution", "Item Discount"),
      ],
    },
    text("inclusionReason", "Inclusion Reason", { width: 300 }),
  ];
}

// THE reconciliation tool: row-level transaction/order-item audit from GET /reports/detail.
export default function TransactionAuditReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const [mode, setMode] = useState<Mode>("transactions");
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const gridRef = useRef<GridApi>();

  const reportType = mode === "transactions" ? "TransactionsList" : "OrderItemsList";
  const state = useDetailQuery(toQueryParams(reportType, filters, { limit }));

  const colDefs = useMemo(
    () => (mode === "transactions" ? transactionColumns() : orderItemColumns()),
    [mode]
  );

  const handleExport = (data: DetailResponse) => {
    const totalMetrics = Object.keys(data.totals) as MetricKey[];
    gridRef.current?.exportDataAsCsv({
      fileName: `TransactionAudit_${reportType}_${data.window.from}_to_${data.window.to}.csv`,
      allColumns: true,
      prependContent: [
        ...buildMethodologyCsvRows(totalMetrics, data.window),
        [{ data: { value: `Rows exported: ${data.rowCount}${data.truncated ? " (TRUNCATED — Totals still cover the full set)" : ""}` } }],
        [{ data: { value: "" } }],
      ],
    });
  };

  return (
    <ReportPageShell
      title="Transaction Audit (Reconciliation)"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
      actions={
        <>
          <ToggleButton
            isSelected={mode === "transactions"}
            onClick={() => setMode("transactions")}
          >
            Transactions
          </ToggleButton>
          <ToggleButton isSelected={mode === "items"} onClick={() => setMode("items")}>
            Order Items
          </ToggleButton>
          <label className="ml-2 flex items-center gap-1 text-sm text-gray-600">
            Row limit
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="rounded-md border border-gray-300 px-2 py-1 text-sm"
            >
              {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n.toLocaleString()}
                </option>
              ))}
            </select>
          </label>
        </>
      }
    >
      {(data) => {
        const totalMetrics = TOTAL_METRICS[mode].filter((m) => m in data.totals);
        return (
          <>
            <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {totalMetrics.map((m) => (
                <StatCard
                  key={m}
                  label={METRIC_LABELS[m]}
                  value={formatMetric(m, data.totals[m])}
                  emphasis={m === "NetSales" || m === "ItemRevenue"}
                />
              ))}
            </div>

            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-gray-600">
                {data.rowCount.toLocaleString()} rows
                {data.truncated && (
                  <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                    TRUNCATED at {limit.toLocaleString()} — Totals above cover the FULL set; raise the
                    limit or narrow the filters to see every row.
                  </span>
                )}
              </div>
              <SecondaryButton onClick={() => handleExport(data)}>Export to CSV</SecondaryButton>
            </div>

            <MethodologyPanel metrics={Object.keys(data.totals) as MetricKey[]} window={data.window} />

            <div style={{ height: "62vh" }}>
              <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
                <AgGridReact
                  rowData={data.rows as any[]}
                  columnDefs={colDefs as ColDef[]}
                  defaultColDef={{ sortable: true, filter: true, resizable: true, minWidth: 90 }}
                  rowHeight={36}
                  pagination
                  paginationPageSize={100}
                  enableCellTextSelection
                  // Excluded rows shaded + muted so what's IN vs OUT is obvious at a glance.
                  getRowStyle={(p) =>
                    p.data && (p.data as { included: boolean }).included === false
                      ? { backgroundColor: "#fef2f2", color: "#9ca3af" }
                      : undefined
                  }
                  onGridReady={(p) => (gridRef.current = p.api)}
                />
              </div>
            </div>
          </>
        );
      }}
    </ReportPageShell>
  );
}
